import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { GetKYCInvestorInstanceRes } from '@dealstack/shared/lib/generated';
import { useMemo } from 'react';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

interface DeclarationsRequired {
	isBeneficialOwnershipRequired?: boolean;
	isSourceOfFundsRequired?: boolean;
	isPepRequired?: boolean;
}

export const useDeclarations = (
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>,
	tInvestor: TranslateFunction<I18Namespaces.INVESTOR>,
	currentInvestor?: GetKYCInvestorInstanceRes,
	requirements?: DeclarationsRequired
) => {
	return useMemo(() => {
		const beneficialDeclaration = currentInvestor?.declarations?.find(
			(declaration) => declaration.type === 'BENEFICIAL_OWNERSHIP'
		);
		const PepDeclaration = currentInvestor?.declarations?.find(
			(declaration) => declaration.type === 'POLITICALLY_EXPOSED_PERSON'
		);
		const isPep = PepDeclaration?.checked;

		const FUNDS_SOURCE_DESCRIPTION = {
			SOURCE_OF_FUNDS_SALARY: tMipManage('kyc.modal.declarations.salary'),
			SOURCE_OF_FUNDS_INHERITANCE: tMipManage('kyc.modal.declarations.inheritance'),
			SOURCE_OF_FUNDS_INVESTMENT_INCOME: tMipManage(
				'kyc.modal.declarations.investment_income'
			),
			SOURCE_OF_FUNDS_PERSONAL_SAVINGS: tMipManage('kyc.modal.declarations.personal_savings'),
			SOURCE_OF_FUNDS_OTHER: tMipManage('kyc.modal.declarations.other'),
		};

		const fundsSources = currentInvestor?.declarations
			?.filter(
				(declaration) =>
					declaration.type.startsWith('SOURCE_OF_FUNDS') && declaration.checked
			)
			?.map((declaration) => {
				const name =
					FUNDS_SOURCE_DESCRIPTION[
						declaration.type as keyof typeof FUNDS_SOURCE_DESCRIPTION
					];
				if (declaration.type !== 'SOURCE_OF_FUNDS_OTHER') {
					return name;
				}
				return `${name}: ${declaration.description}`;
			})
			?.sort((a, b) => {
				if (a.includes(':')) {
					return 1;
				}
				if (b.includes(':')) {
					return -1;
				}
				return a.localeCompare(b);
			})
			?.join(', ');

		const getPepStatus = () => {
			if (isPep === null) {
				return LONG_DASH;
			}
			if (isPep) {
				return `${tMipManage('kyc.modal.declarations.yes')}: ${PepDeclaration.description}`;
			}

			return tMipManage('kyc.modal.declarations.no');
		};

		const isBoRequired = requirements?.isBeneficialOwnershipRequired ?? true;
		const isPepRequired = requirements?.isPepRequired ?? true;
		const isSofRequired = requirements?.isSourceOfFundsRequired ?? true;

		const declarations = [
			...(isBoRequired
				? [
						{
							id: 'beneficial-ownership',
							label: tInvestor(
								`investment.join.kyc.declarations.beneficial.${currentInvestor?.piv ? 'label_piv' : 'label_individual'}`
							),
							value:
								beneficialDeclaration?.checked == null
									? LONG_DASH
									: tMipManage(
											`kyc.modal.declarations.${beneficialDeclaration?.checked ? 'yes' : 'no'}`
										),
						},
					]
				: []),
			...(isPepRequired
				? [
						{
							id: 'politically-exposed-person',
							label: tMipManage('kyc.modal.declarations.are_pip'),
							value: getPepStatus(),
						},
					]
				: []),
			...(isSofRequired
				? [
						{
							id: 'source-of-funds',
							label: tMipManage('kyc.modal.declarations.what_source_salary'),
							value: fundsSources || LONG_DASH,
						},
					]
				: []),
		];

		return declarations;
	}, [tMipManage, tInvestor, currentInvestor, requirements]);
};
